// external
import { useContext, useState, useEffect, useCallback, memo } from "react";
import Select from "react-select";
import PropTypes from "prop-types";

// components
import CustomSelect from "components/partials/custom-select/custom-select";
import CustomButton from "components/partials/custom-button/custom-button";

// api
import apiRoot from "api/apiRoot";

// state managing/security
import { FaoRegimeContext } from "context/FaoRegimeContext";
import { ClientContext } from "context/ClientContext";

// data/constants
import { OPERATIONS } from "../constants/operations.constants";
import { COLUMN_OPTIONS } from "../constants/column-options.constants";

// assets
import Icons from "assets/icons";

// css/styling
import { STYLES } from "../constants/styles.constants";

const LogicDeleteButton = memo(({ isEditable, onDelete }) => {
  if (!isEditable) return null;

  return (
    <div className="mx-[-30px]">
      <CustomButton
        disabled={!isEditable}
        iconBefore={<Icons.Plus customClass="h-4 w-4 rotate-45" />}
        onClick={onDelete}
        customClass="text-blue-01 size-2 hover:text-blue-03 transition-all"
      />
    </div>
  );
});

LogicDeleteButton.propTypes = {
  isEditable: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const LogicSelectors = memo(
  ({
    column,
    operation,
    valueOptions,
    value,
    isEditable,
    isDisplay,
    onColumnChange,
    onOperationChange,
    onValueChange,
  }) => (
    <div className="width-[200px] ml-2 flex items-end gap-2 pl-5">
      <CustomSelect
        label="Matrix Logic"
        id="matrixLogic"
        options={COLUMN_OPTIONS}
        value={column}
        labelInTop={true}
        required={true}
        customClass={`${STYLES.label} mb-2`}
        customSelectClass="h-10 w-[200px]"
        onChange={onColumnChange}
        extraStyles={() => STYLES.select}
        disabled={!isEditable}
      />
      <CustomSelect
        id="matrixLogicOperation"
        options={OPERATIONS}
        value={operation}
        labelInTop={true}
        required={true}
        customClass={`${STYLES.label} mb-2.5`}
        customSelectClass="h-10 w-[200px]"
        onChange={onOperationChange}
        extraStyles={() => STYLES.select}
        disabled={!isEditable}
      />
      <Select
        key={operation}
        isMulti={true}
        isCreatable={false}
        options={valueOptions}
        value={value.map((cur) => ({ value: cur, label: cur }))}
        isLoading={valueOptions.length === 0 && !isDisplay && isEditable}
        isDisabled={valueOptions.length === 0 || !isEditable || isDisplay}
        onChange={onValueChange}
        styles={STYLES.select}
      />
    </div>
  )
);

LogicSelectors.propTypes = {
  column: PropTypes.string.isRequired,
  operation: PropTypes.string.isRequired,
  valueOptions: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  isEditable: PropTypes.bool.isRequired,
  isDisplay: PropTypes.bool.isRequired,
  onColumnChange: PropTypes.func.isRequired,
  onOperationChange: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
};

function RegimeLogicViewer({ matrixInd, logicInd, isEditable, isDisplay }) {
  const { currentClientData } = useContext(ClientContext);
  const [columnSelected, setColumnSelected] = useState(null);
  const {
    matrices,
    setMatrices,
    currentlyEditing,
    columnOptions,
    setColumnOptions,
    compare,
  } = useContext(FaoRegimeContext);
  const [valueOptions, setValueOptions] = useState([]);
  useEffect(() => {
    setColumnOptions(COLUMN_OPTIONS);
  }, [setColumnOptions]);

  useEffect(() => {
    const fetchColumnValues = async () => {
      setValueOptions([]);
      const currentMatrix = matrices[currentlyEditing];
      const currentLogic = currentMatrix.logic[logicInd];

      if (!currentLogic.schema || !currentLogic.table || !currentLogic.column) {
        return;
      }

      try {
        const res = await apiRoot.get(
          `/api/fao-regime/columnvalues/${currentLogic.schema}/${currentLogic.table}/${currentLogic.column}/${currentClientData.schoolKey}`
        );
        if (res.data.length === 0) {
          setValueOptions([
            { value: "No values", label: "No values", isDisabled: true },
          ]);
        } else {
          const newValueOptions = res.data
            .map((item) => ({ value: item.Value, label: item.Value }))
            .sort(compare);
          setValueOptions(newValueOptions);
        }
      } catch (error) {
        console.error("Error fetching column values:", error);
      }
    };

    fetchColumnValues();
  }, [columnSelected]);

  const onSelectLogicColumn = useCallback(
    (selectedVal) => {
      const { schema, table, value } = selectedVal;
      setColumnSelected(value);
      const updatedMatrices = JSON.parse(JSON.stringify(matrices));
      const currentMatrix = updatedMatrices[currentlyEditing];
      currentMatrix.logic[logicInd] = {
        ...currentMatrix.logic[logicInd],
        column: value,
        schema,
        table,
        value: [],
      };
      setMatrices(updatedMatrices);
    },
    [matrices, currentlyEditing, logicInd, setMatrices]
  );

  const onSelectLogicOperation = useCallback(
    (selectedVal) => {
      const updatedMatrices = JSON.parse(JSON.stringify(matrices));
      const currentMatrix = updatedMatrices[currentlyEditing];
      currentMatrix.logic[logicInd].operation = selectedVal.value;
      setMatrices(updatedMatrices);
    },
    [matrices, currentlyEditing, logicInd, setMatrices]
  );

  const onSelectLogicValue = useCallback(
    (selectedVal) => {
      const updatedMatrices = JSON.parse(JSON.stringify(matrices));
      const currentMatrix = updatedMatrices[currentlyEditing];
      const isMultiSelectOp = OPERATIONS.find(
        (val) => currentMatrix.logic[logicInd].operation === val.value
      ).isMultiSelect;

      currentMatrix.logic[logicInd].value = isMultiSelectOp
        ? Array.from(selectedVal, (option) => option.value)
        : selectedVal && selectedVal.length > 0
        ? [selectedVal[selectedVal.length - 1].value]
        : [];

      setMatrices(updatedMatrices);
    },
    [matrices, currentlyEditing, logicInd, setMatrices]
  );

  const handleAndLogic = useCallback(() => {
    const updatedMatrices = JSON.parse(JSON.stringify(matrices));
    const currentMatrix = updatedMatrices[currentlyEditing];
    currentMatrix.logic.push({
      column: "",
      operation: "=",
      value: [],
    });
    setMatrices(updatedMatrices);
  }, [matrices, currentlyEditing, setMatrices]);

  const handleDeleteLogic = useCallback(() => {
    const updatedMatrices = JSON.parse(JSON.stringify(matrices));
    const currentMatrix = updatedMatrices[currentlyEditing];
    currentMatrix.logic.splice(logicInd, 1);

    if (currentMatrix.logic.length === 0) {
      alert("Must have at least one matrix logic");
      return;
    }

    setMatrices(updatedMatrices);
  }, [matrices, currentlyEditing, logicInd, setMatrices]);

  const currentLogic = matrices[matrixInd].logic[logicInd];

  return (
    <div className="flex items-end">
      <LogicDeleteButton isEditable={isEditable} onDelete={handleDeleteLogic} />
      <LogicSelectors
        column={currentLogic.column}
        operation={currentLogic.operation}
        valueOptions={valueOptions}
        value={currentLogic.value}
        isEditable={isEditable}
        isDisplay={isDisplay}
        onColumnChange={(selectedVal) => onSelectLogicColumn(selectedVal)}
        onOperationChange={(selectedVal) => onSelectLogicOperation(selectedVal)}
        onValueChange={(selectedVal) => onSelectLogicValue(selectedVal)}
      />
      <CustomButton
        variant="secondary"
        size="md"
        onClick={handleAndLogic}
        disabled={!isEditable}
        customClass="mb-1 h-9 ml-2"
      >
        AND
      </CustomButton>
    </div>
  );
}

RegimeLogicViewer.propTypes = {
  matrixInd: PropTypes.number.isRequired,
  logicInd: PropTypes.number.isRequired,
  isEditable: PropTypes.bool.isRequired,
  isDisplay: PropTypes.bool.isRequired,
};

export default memo(RegimeLogicViewer);
